import { Component } from '@theme/component';

class EvermarkdPersonalization extends Component {
  #controller;
  #observer;
  #scope;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('input', this.#onInput, options);
    this.addEventListener('change', this.#onInput, options);
    this.addEventListener('invalid', this.#onInvalid, { ...options, capture: true });
    this.addEventListener('click', this.#onPreviewReturn, options);
    this.#scope = this.closest('.shopify-section, dialog') || this.closest('product-form-component') || this;
    this.#scope.addEventListener('input', this.#onSelection, options);
    this.#scope.addEventListener('change', this.#onSelection, options);
    this.#scope.addEventListener('submit', this.#syncPatchBorder, { ...options, capture: true });
    if (this.dataset.personalizedBuilder === 'true') {
      window.addEventListener('resize', this.#syncSticky, options);
      const grid = this.closest('.product-information--personalized-builder');
      const media = grid?.querySelector('.product-information__media');
      if (media && typeof ResizeObserver !== 'undefined') {
        this.#resizeObserver = new ResizeObserver(this.#syncSticky);
        this.#resizeObserver.observe(media);
        const header = document.getElementById('header-group');
        if (header) this.#resizeObserver.observe(header);
      }
    }
    const selectionSelector = '.gpo-app, gpo-container, .gpo-container, variant-picker, input[data-field-name="Hat Color"], input[data-option-name], select[name^="options["]';
    this.#observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (this.contains(record.target) && !record.target.closest?.('.gpo-app, gpo-container, .gpo-container, [data-personalization-color-slot]')) return false;
        if (record.type === 'attributes') return record.target.matches?.(selectionSelector) || record.target.closest?.('variant-picker, .gpo-app');
        if (record.target.closest?.('variant-picker, .gpo-app')) return true;
        return [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (node.matches(selectionSelector) || node.querySelector(selectionSelector))
        );
      });
      if (relevant) {
        this.#syncBuilder();
        this.#updateSummary();
      }
    });
    this.#observer.observe(this.#scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'selected', 'value'] });
    this.#syncBuilder();
    this.#syncPatchBorder();
    this.#updateSummary();
  }

  #syncPatchBorder = () => {
    if (this.dataset.personalizedBuilder !== 'true') return;
    const options = this.querySelector('[data-personalized-patch-border-options]');
    if (!options) return;
    const shape = this.querySelector('[data-personalized-patch-shape]:checked')?.value;
    const excluded = shape === 'Custom Contour';
    if (excluded) {
      for (const input of options.querySelectorAll('[data-personalized-patch-border]')) {
        input.checked = input.value === 'No border';
      }
    }
    options.disabled = excluded;
  };

  #syncBuilder() {
    if (this.dataset.personalizedBuilder !== 'true') return;
    const form = this.closest('form');
    const slot = this.querySelector('[data-personalization-color-slot]');
    if (!form || !slot) return;
    const colors = [...form.querySelectorAll('.gpo-app input[data-field-name="Hat Color"]')];
    const color = colors.find((input) => !slot.contains(input)) || colors[0];
    const container = color?.closest('gpo-container, .gpo-container') || color?.closest('.gpo-app');
    if (container && container.closest('form') === form && !slot.contains(container)) {
      const focused = container.contains(document.activeElement) ? document.activeElement : null;
      slot.hidden = false;
      slot.append(container);
      focused?.focus({ preventScroll: true });
    }
    slot.hidden = !slot.querySelector('.gpo-app input[data-field-name="Hat Color"]');
    this.#syncSticky();
  }

  #syncSticky = () => {
    if (this.dataset.personalizedBuilder !== 'true') return;
    const grid = this.closest('.product-information--personalized-builder');
    const media = grid?.querySelector('.product-information__media');
    if (!media) return;
    const headerHeight = Number.parseFloat(getComputedStyle(media).getPropertyValue('--header-height')) || 0;
    const fits = media.getBoundingClientRect().height <= window.innerHeight - headerHeight - 32;
    grid.toggleAttribute('data-builder-sticky-fit', fits);
  };

  #isAppField(field) {
    return Boolean(field.closest?.('.gpo-app, gpo-container, .gpo-container'));
  }

  #onPreviewReturn = (event) => {
    if (!event.target.closest?.('[data-personalization-preview-return]')) return;
    const preview = this.#scope.querySelector('evermarkd-product-preview[data-personalized-patch="true"]:not([hidden])') || this.#scope.querySelector('media-gallery:not([hidden])');
    if (!preview) return;
    preview.setAttribute('tabindex', '-1');
    preview.setAttribute('role', 'region');
    preview.setAttribute('aria-label', this.dataset.previewRegion || '');
    preview.scrollIntoView({ behavior: 'auto', block: 'start' });
    preview.focus({ preventScroll: true });
  };

  #onInput = (event) => {
    this.#syncPatchBorder();
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (this.#isAppField(field)) {
      this.#updateSummary();
      return;
    }
    field.setCustomValidity('');
    if (field.required && field.type !== 'file' && !field.value.trim()) {
      field.setCustomValidity(this.dataset.requiredError || '');
    }
    if (field.type === 'file' && field instanceof HTMLInputElement && field.files?.length) {
      const file = field.files[0];
      if (file.size > 10 * 1024 * 1024 || !/\.(jpe?g|png|pdf)$/i.test(file.name)) {
        field.setCustomValidity(this.dataset.fileError || '');
      }
    }
    this.#showError(field, !field.validity.valid);
    this.#updateSummary();
  };

  #onSelection = (event) => {
    if (!this.contains(event.target)) this.#updateSummary();
  };

  #onInvalid = (event) => {
    const field = event.target;
    if (this.#isAppField(field)) return;
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) this.#showError(field, true);
  };

  #showError(field, invalid) {
    const error = this.querySelector(`[id="${field.id}-error"]`);
    field.setAttribute('aria-invalid', String(invalid));
    if (!error) return;
    error.hidden = !invalid;
    error.textContent = invalid ? field.validationMessage || this.dataset.requiredError : '';
  }

  #updateSummary() {
    const list = this.querySelector('[data-personalization-summary]');
    if (!list) return;
    const entries = new Map();
    for (const field of this.#scope.querySelectorAll('variant-picker input[data-option-name]:checked, variant-picker select[name^="options["]')) {
      const label = field.dataset.optionName || field.selectedOptions?.[0]?.dataset.optionName;
      if (label && field.value) entries.set(label, field.value);
    }
    const selectionScope = this.dataset.personalizedBuilder === 'true' ? this.closest('form') || this.#scope : this.#scope;
    const hatColor = selectionScope.querySelector('.gpo-app input[data-field-name="Hat Color"]:checked');
    if (hatColor?.value) entries.set(hatColor.dataset.fieldName, hatColor.value);
    if (this.dataset.personalizedBuilder === 'true') {
      const shape = selectionScope.querySelector('[data-personalized-patch-shape]:checked');
      for (const link of this.#scope.querySelectorAll('[data-artwork-review-link]')) {
        const url = new URL(link.href, window.location.origin);
        if (hatColor?.value) url.searchParams.set('color', hatColor.value);
        else url.searchParams.delete('color');
        if (shape?.value) url.searchParams.set('shape', shape.value);
        else url.searchParams.delete('shape');
        link.href = url.pathname + url.search + url.hash;
      }
    }
    const colorSummary = this.querySelector('[data-builder-color-summary]');
    if (colorSummary) colorSummary.textContent = hatColor?.value ? `${hatColor.dataset.fieldName}: ${hatColor.value}` : this.dataset.chooseColor || '';
    for (const field of this.querySelectorAll('[data-summary-label]')) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      if (this.#isAppField(field) || field.matches(':disabled')) continue;
      if (field instanceof HTMLInputElement && ['radio', 'checkbox'].includes(field.type) && !field.checked) continue;
      const value = field instanceof HTMLInputElement && field.type === 'file' ? field.files?.[0]?.name : field.value.trim();
      if (value) entries.set(field.dataset.summaryLabel, value);
    }
    const previous = Array.from(list.children).map((node) => node.textContent);
    const next = Array.from(entries).flat();
    if (JSON.stringify(previous) !== JSON.stringify(next)) {
      const fragment = document.createDocumentFragment();
      for (const [label, value] of entries) {
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = label;
        detail.textContent = value;
        fragment.append(term, detail);
      }
      list.replaceChildren(fragment);
    }
    const empty = this.querySelector('[data-empty-summary]');
    if (empty) empty.hidden = list.children.length > 0;
  }
}

if (!customElements.get('evermarkd-personalization')) customElements.define('evermarkd-personalization', EvermarkdPersonalization);


